/**
 * Date formatting utilities — 프론트 전용 함수.
 *
 * 웹·모바일이 같이 쓰는 날짜 함수의 정본은 `@aegislab/shared` 다.
 * 그중 프론트가 쓰는 이름(아래 export 블록)만 다시 내보낸다.
 */

import { addDays, format, parseISO } from 'date-fns';
import { datetimeLocalToKstISO } from '@aegislab/shared';

export {
  WEEKDAYS,
  getLocalToday,
  getLocalDateString,
  getLocalMonthStart,
  getLocalWeekStart,
  getBusinessDaysUpToToday,
  datetimeLocalToKstISO,
  toUtcDateString as getUtcDateString,
  getKstDateString,
  getKstMonthDay,
  getKstDateAfterDays,
  formatDateDotKo,
  formatMonthDayKo,
} from '@aegislab/shared';

/**
 * Convert HH:mm time on an anchor date to KST ISO, inferring next-day rollover
 * when `time` is earlier than `reference` (typically the work-shift start).
 *
 * Used for cross-midnight attendance shifts where the UI represents both
 * clock-in and clock-out as HH:mm strings anchored to a single calendar day.
 *
 * Contract: `time`/`reference` must be zero-padded HH:mm (string `<` compares
 * lexicographically). Assumes a single shift shorter than 24h that crosses
 * midnight at most once — with only HH:mm (no date) it cannot distinguish the
 * same HH:mm on consecutive days, so a >24h span is out of scope.
 *
 * Returns null for empty `time`.
 */
export function toIsoWithDayInference(
  date: string, // YYYY-MM-DD
  time: string, // HH:mm
  reference: string // HH:mm — typically clockInTime; '' disables inference
): string | null {
  if (!time) return null;
  const isNextDay = reference !== '' && time < reference;
  const anchor = isNextDay ? format(addDays(parseISO(date), 1), 'yyyy-MM-dd') : date;
  return datetimeLocalToKstISO(`${anchor}T${time}`);
}
